/* What a purchase is, and what it lets you do. */

export const IN_PROGRESS = "in_progress";
export const OPEN = "open";
export const EXPIRED = "expired";

/* Is TREVEX still building this purchase's report? */
export const worldboxStatus = (worldboxOrders, companyID) => {
  if (!companyID) return "no_worldbox";
  const matches = (worldboxOrders || []).filter(
    (item) => item?.companyID === companyID
  );
  if (matches.length > 0) {
    return matches[0].status === "available" ? "no_worldbox" : matches[0].status;
  }
  return "no_worldbox";
};

/* An order-backed row reports its own progress. */
export const ORDER_COMPLETE = "COMPLETED";

export const orderStatus = (row) => {
  const status = row?.status;
  if (!status) return "no_worldbox";
  return status === ORDER_COMPLETE ? "no_worldbox" : status;
};

/* The one question the row asks: is anything still being built for it? */
export const reportStatus = (row, worldboxOrders) =>
  row?.purchaseID ? worldboxStatus(worldboxOrders, row?.companyID) : orderStatus(row);

/* The three states are exclusive, which the live screen's two independent
   signals are not: a row can be both "no_worldbox" and expired. Ordered so an
   outstanding report wins — there is nothing to expire until it exists. */
export const purchaseState = (row, status) => {
  if (status !== "no_worldbox") return IN_PROGRESS;
  if (row?.is_expired) return EXPIRED;
  return OPEN;
};

/* Transcribed merge from Purchased.jsx: a TREVEX purchase is used as it stands; anything else is replaced by the order it points at, matched on `+order_id ===... */
export const mergePurchases = (payload) => {
  const purchases = payload?.data || [];
  const orders = payload?.orders || [];

  return purchases
    .map((item) =>
      String(item?.companyID || "").startsWith("TRX")
        ? item
        : orders.find((order) => +item?.order_id === order?.id)
    )
    .filter(Boolean)
    .slice()
    .sort((a, b) => {
      const dateA = new Date(a.purchaseDate || a.dateCreated);
      const dateB = new Date(b.purchaseDate || b.dateCreated);
      return dateB - dateA;
    });
};

/* How long is left on an open purchase. */
export const daysLeft = (expireDate, today = new Date()) => {
  if (!expireDate) return null;
  const end = new Date(expireDate);
  if (Number.isNaN(end.getTime())) return null;

  const startOfDay = (d) => Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  const diff = startOfDay(end) - startOfDay(today);
  const days = Math.ceil(diff / 86400000);
  return days > 0 ? days : 0;
};

/* "04 Jul 2026", as the design writes dates on this screen. */
export const purchasedOn = (row) => {
  const value = row?.purchaseDate || row?.dateCreated;
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

/* The strip under a row. `statusLabel` is the company's own profile status ("Claimed" / "Verified"), which only the row knows — it is what its own lookup... */
export const purchaseNote = (state, { statusLabel, purchasedOn: on, analystBuilt } = {}) => {
  if (state === IN_PROGRESS) {
    return {
      tone: "progress",
      text: "TREVEX is building this report · delivery within 24 hours",
    };
  }

  if (state === EXPIRED) {
    return {
      tone: "muted",
      text: on ? `Purchased ${on} — access window closed` : "Access window closed",
    };
  }

  if (analystBuilt) {
    return {
      tone: "done",
      text: on
        ? `Report built by TREVEX analysts · purchased ${on}`
        : "Report built by TREVEX analysts",
    };
  }

  if (statusLabel) {
    return {
      tone: "done",
      text: `Opened straight away — this business is ${statusLabel}`,
    };
  }

  return null;
};

/* The filter chips, and what each one keeps. `all` is the design's default. */
export const PURCHASE_FILTERS = [
  { id: "all", label: "All" },
  { id: IN_PROGRESS, label: "Report in progress" },
  { id: OPEN, label: "Open now" },
  { id: EXPIRED, label: "Expired" },
];

export const matchesFilter = (state, filter) => filter === "all" || state === filter;
